import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  FiAlertCircle, FiArrowLeft, FiCheckCircle, FiKey, FiLock, FiLogIn, FiMail, FiShield, FiX,
} from 'react-icons/fi';
import authService from '../utils/auth.js';
import AuthShell from './AuthShell.jsx';

function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [userId, setUserId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const maybeShowTrialLoginNotice = (trial) => {
    if (!trial?.isTrialUser || !trial?.isTrialActive) return null;
    const trialEndsAt = trial?.trialEndsAt ? new Date(trial.trialEndsAt).toLocaleString() : 'N/A';
    const notice = `Your 3-day trial has started. You currently have trial admin access. Trial ends on ${trialEndsAt}.`;
    if (typeof window !== 'undefined') {
      window.alert(notice);
    }
    return notice;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      setShowTwoFactor(false);
      setUserId(null);
      setTwoFactorCode('');

      if (!email.trim()) {
        throw new Error('Please enter your email address');
      }
      if (!validateEmail(email)) {
        throw new Error('Please enter a valid email address');
      }
      if (!password.trim()) {
        throw new Error('Please enter your password');
      }

      const response = await authService.login(email, password);

      if (response.token) {
        const trialNotice = maybeShowTrialLoginNotice(response?.user?.trial);
        // Direct login successful - redirect to chat
        if (response.message && response.message.includes('2FA session valid')) {
          setSuccessMessage(trialNotice || 'Login successful! (2FA session valid) Redirecting...');
        } else {
          setSuccessMessage(trialNotice || 'Login successful! Redirecting...');
        }
        setTimeout(() => navigate('/'), 1000);
      } else if (response.userId) {
        // 2FA required
        setUserId(response.userId);
        setShowTwoFactor(true);
        setSuccessMessage('2FA code has been sent to your email');
      }
    } catch (err) {
      setError(err.message);
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorVerification = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      if (!twoFactorCode.trim()) {
        throw new Error('Please enter the 2FA code from your email');
      }

      const response = await authService.verify2FA(userId, twoFactorCode);
      if (response.token) {
        const trialNotice = maybeShowTrialLoginNotice(response?.user?.trial);
        setSuccessMessage(trialNotice || 'Login successful! Redirecting...');
        setTimeout(() => navigate('/'), 1000);
      }
    } catch (err) {
      setError(err.message);
      setTwoFactorCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="auth-card">
        <div className="auth-header">
          <span className="auth-icon" aria-hidden="true">
            {showTwoFactor ? <FiShield /> : <FiLogIn />}
          </span>
          <h1>{showTwoFactor ? 'Check your email' : 'Welcome back'}</h1>
          <p>{showTwoFactor ? 'Enter the 6-digit authentication code we sent you' : 'Sign in to NovaBotX™ to continue'}</p>
        </div>

        {error && (
          <div className="auth-alert auth-alert-error" role="alert">
            <FiAlertCircle className="auth-alert-icon" aria-hidden="true" />
            <span className="auth-alert-text">{error}</span>
            <button type="button" onClick={() => setError('')} className="auth-alert-close" aria-label="Dismiss"><FiX /></button>
          </div>
        )}

        {successMessage && (
          <div className="auth-alert auth-alert-success" role="status">
            <FiCheckCircle className="auth-alert-icon" aria-hidden="true" />
            <span className="auth-alert-text">{successMessage}</span>
            <button type="button" onClick={() => setSuccessMessage('')} className="auth-alert-close" aria-label="Dismiss"><FiX /></button>
          </div>
        )}

        <form onSubmit={showTwoFactor ? handleTwoFactorVerification : handleLogin} className="auth-form" key={showTwoFactor ? '2fa' : 'login'}>
          {!showTwoFactor ? (
            <>
              <div className="auth-form-group">
                <label htmlFor="email">Email address</label>
                <div className="auth-input">
                  <FiMail aria-hidden="true" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    autoComplete="email"
                    placeholder="you@company.com"
                  />
                </div>
              </div>
              <div className="auth-form-group">
                <div className="auth-label-row">
                  <label htmlFor="password">Password</label>
                  <Link to="/forgot-password" className="auth-link auth-link-sm">Forgot password?</Link>
                </div>
                <div className="auth-input">
                  <FiLock aria-hidden="true" />
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="Enter your password"
                  />
                </div>
              </div>
              <button type="submit" className="auth-button auth-button-primary" disabled={loading}>
                {loading && <span className="auth-spinner" aria-hidden="true" />}
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
              <div className="auth-links">
                <span className="auth-links-text">New to NovaBotX™?</span>
                <Link to="/register" className="auth-link">Create New Account</Link>
              </div>
            </>
          ) : (
            <>
              <div className="auth-form-group">
                <label htmlFor="twoFactorCode">Security code</label>
                <div className="auth-input">
                  <FiKey aria-hidden="true" />
                  <input
                    type="text"
                    id="twoFactorCode"
                    name="twoFactorCode"
                    className="auth-code-input"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    autoFocus
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="Enter 6-digit code"
                    maxLength="6"
                  />
                </div>
                <p className="auth-help-text">Check your email for the verification code</p>
              </div>
              <button type="submit" className="auth-button auth-button-primary" disabled={loading}>
                {loading && <span className="auth-spinner" aria-hidden="true" />}
                {loading ? 'Verifying...' : 'Verify Code'}
              </button>
              <button
                type="button"
                className="auth-button auth-button-secondary"
                onClick={() => {
                  setShowTwoFactor(false);
                  setTwoFactorCode('');
                  setUserId(null);
                }}
              >
                <FiArrowLeft aria-hidden="true" /> Back to Login
              </button>
            </>
          )}
        </form>
      </div>
    </AuthShell>
  );
}

export default Login;
